/**
 * Analytics Page
 * Workspace analytics dashboard (admin-only)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import * as analyticsService from '../services/analytics.service';

type Period = '7d' | '30d' | '90d' | 'all';
type ExportFormat = 'csv' | 'json';

export const AnalyticsPage = () => {
  const [period, setPeriod] = useState<Period>('30d');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [isExporting, setIsExporting] = useState(false);

  // Calculate date range based on period
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();

    switch (period) {
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
      case 'all':
        start.setFullYear(2020, 0, 1); // Far back date
        break;
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const { startDate, endDate } = getDateRange();

  // Fetch message volume
  const { data: volumeData, isLoading: volumeLoading } = useQuery({
    queryKey: ['analytics', 'volume', period],
    queryFn: () =>
      analyticsService.getMessageVolume({
        period: 'daily',
        startDate,
        endDate,
      }),
  });

  // Fetch active users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['analytics', 'users', period],
    queryFn: () =>
      analyticsService.getActiveUsers({
        period,
        limit: 10,
      }),
  });

  // Fetch active channels
  const { data: channelsData, isLoading: channelsLoading } = useQuery({
    queryKey: ['analytics', 'channels', period],
    queryFn: () =>
      analyticsService.getActiveChannels({
        period,
        limit: 10,
      }),
  });

  // Fetch response time
  const { data: responseData, isLoading: responseLoading } = useQuery({
    queryKey: ['analytics', 'response', period],
    queryFn: () =>
      analyticsService.getResponseTime({
        period,
      }),
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await analyticsService.downloadAnalytics({
        format: exportFormat,
        metrics: 'all',
        startDate,
        endDate,
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Грешка при експортиране на данни');
    } finally {
      setIsExporting(false);
    }
  };

  const isLoading = volumeLoading || usersLoading || channelsLoading || responseLoading;

  // Format chart data
  const volumeChartData =
    volumeData?.analytics.map((item) => ({
      date: new Date(item.timestamp).toLocaleDateString('bg-BG', {
        month: 'short',
        day: 'numeric',
      }),
      count: item.count,
    })) || [];

  const usersChartData =
    usersData?.activeUsers.map((user) => ({
      name: user.displayName,
      messages: user.messageCount,
    })) || [];

  const channelsChartData =
    channelsData?.activeChannels.map((channel) => ({
      name: channel.name,
      messages: channel.messageCount,
      users: channel.uniqueUsers,
    })) || [];

  return (
    <div className="h-screen overflow-y-auto bg-gray-50 dark:bg-dark-bg-primary">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">
            Аналитика на работното пространство
          </h1>
          <p className="text-gray-600 dark:text-dark-text-secondary">
            Статистика и тенденции за активността във вашия екип
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 dark:text-dark-text-primary">
                Период:
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as Period)}
                className="px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg text-sm bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Последните 7 дни</option>
                <option value="30d">Последните 30 дни</option>
                <option value="90d">Последните 90 дни</option>
                <option value="all">Всички</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                className="px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg text-sm bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary"
              >
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
              </select>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? 'Експортиране...' : 'Експорт на данни'}
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-dark-text-secondary">Зареждане...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                  Общо съобщения
                </h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                  {volumeData?.summary.totalMessages.toLocaleString('bg-BG') || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                  Активни потребители
                </h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                  {usersData?.totalActiveUsers || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                  Средно време за отговор
                </h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                  {responseData?.globalAverage
                    ? `${Math.round(responseData.globalAverage / 60)} мин`
                    : '0 мин'}
                </p>
              </div>

              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                  Активни канали
                </h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                  {channelsData?.activeChannels.length || 0}
                </p>
              </div>
            </div>

            {/* Message Volume Chart */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Обем на съобщенията
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={volumeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" name="Съобщения" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Active Users and Channels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Active Users */}
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                  Най-активни потребители
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usersChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="messages" fill="#10b981" name="Съобщения" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Active Channels */}
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                  Най-активни канали
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={channelsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="messages" fill="#8b5cf6" name="Съобщения" />
                    <Bar dataKey="users" fill="#f59e0b" name="Потребители" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
